import '@angular/localize';
import * as Handlebars from 'handlebars';
import DOMPurify from 'dompurify';
import moment from 'moment-timezone';
import _ from 'lodash';

export const OMIT_TOKEN = '__RAPIDER_OMIT__';

/**
 * AI-readable custom Handlebars helper descriptions.
 * Use this object in prompts so AI knows which helpers are available.
 */
export const CUSTOM_HANDLEBARS_HELPERS: Record<string, string> = {
  formatDate:
    'Formats a date/timestamp into a readable localized date-time string.',
  or: 'Returns true if at least one provided value is truthy.',
  and: 'Returns true only if all provided values are truthy.',
  not: 'Returns the boolean opposite of a value.',
  eq: 'Returns true when two values are strictly equal.',
  neq: 'Returns true when two values are not strictly equal.',
  gt: 'Returns true when the first value is greater than the second value.',
  gte: 'Returns true when the first value is greater than or equal to the second value.',
  lt: 'Returns true when the first value is less than the second value.',
  lte: 'Returns true when the first value is less than or equal to the second value.',
  coalesce:
    'Returns the first value that is not null, undefined, or an empty string.',
  ternary:
    'Returns the second value when condition is truthy, otherwise returns the third value.',
  math: 'Performs basic math operations: +, -, *, /, %, round.',
  json: 'Converts a value into a formatted JSON string.',
  isEmptyValue:
    'Returns true when a value is null, undefined, empty string, empty array, or empty object.',
  generateId:
    'Generates a random unique string ID. Useful for linking <label for="uid"> and <input id="uid">. Usage: {{#with (generateId "field") as |uid|}} <label for="{{uid}}"> <input id="{{uid}}"> {{/with}}',
  omit: 'Outputs a special token that completely removes the key from the JSON payload when compiling dynamic events.',
  omitIfUndefined:
    'Removes the key from the JSON payload if the value is undefined (untouched by the user).',
  omitIfEmpty:
    'Removes the key from the JSON payload if the value is null, undefined, an empty string, empty array, or empty object.',
};

/**
 * Removes the last Handlebars metadata argument from helper arguments.
 */
function getHelperArgs(args: unknown[]): unknown[] {
  return args.slice(0, -1);
}

/**
 * Converts a value to a number safely.
 */
function toSafeNumber(value: unknown): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

/**
 * Generates a random unique ID string.
 *
 * @param prefix - Optional string prefix for the ID.
 * @returns A unique string like 'field-5x9a2b'.
 */
Handlebars.registerHelper('generateId', function (prefix: unknown) {
  const p = typeof prefix === 'string' ? prefix : 'id';
  return `${p}-${Math.random().toString(36).substring(2, 9)}`;
});

/**
 * OMIT HELPERS: Used to drop keys dynamically from API payloads
 */
Handlebars.registerHelper('omit', function () {
  return OMIT_TOKEN;
});

Handlebars.registerHelper('omitIfUndefined', function (value: unknown) {
  // CRITICAL FIX: Use loose equality (==) to catch BOTH undefined AND null!
  // This ensures that when the modal is closed and state is set to `null`, it still omits safely.
  return value == null ? OMIT_TOKEN : value;
});

Handlebars.registerHelper('omitIfEmpty', function (value: unknown) {
  if (value === null || value === undefined || value === '') return OMIT_TOKEN;
  if (Array.isArray(value) && value.length === 0) return OMIT_TOKEN;
  if (typeof value === 'object' && Object.keys(value).length === 0)
    return OMIT_TOKEN;
  return value;
});

/**
 * Adds all lodash functions as Handlebars helpers.
 * This allows AI-generated templates to use lodash helpers directly.
 * CRITICAL FIX: We MUST exclude keys that conflict with Handlebars native block helpers.
 * Otherwise, lodash's `_.each` will overwrite the Handlebars `{{#each}}` block helper and break all loops.
 */
const restrictedHandlebarsHelpers = [
  'each',
  'if',
  'unless',
  'with',
  'lookup',
  'log',
];

Object.keys(_).forEach((key) => {
  // Skip registering this lodash function if it conflicts with a native Handlebars helper
  if (restrictedHandlebarsHelpers.includes(key)) {
    return;
  }

  const fn = _[key as keyof typeof _];

  if (typeof fn === 'function') {
    Handlebars.registerHelper(key, (...args: unknown[]) => {
      const helperArgs = getHelperArgs(args);
      return (fn as (...innerArgs: unknown[]) => unknown)(...helperArgs);
    });
  }
});

/**
 * Formats a date/timestamp into a readable localized date-time string.
 *
 * @param dateString - Date string or Date object.
 * @returns Formatted date string, original value if invalid, or empty string if missing.
 */
Handlebars.registerHelper('formatDate', function (dateString: string | Date) {
  if (!dateString) return '';

  const parsedDate = moment(dateString);

  if (!parsedDate.isValid()) return dateString;

  return parsedDate.format('lll');
});

/**
 * Returns true if at least one provided value is truthy.
 *
 * @param args - Values to evaluate.
 * @returns True if any value is truthy.
 */
Handlebars.registerHelper('or', function (...args: unknown[]) {
  return getHelperArgs(args).some(Boolean);
});

/**
 * Returns true only if all provided values are truthy.
 *
 * @param args - Values to evaluate.
 * @returns True if every value is truthy.
 */
Handlebars.registerHelper('and', function (...args: unknown[]) {
  return getHelperArgs(args).every(Boolean);
});

/**
 * Returns the boolean opposite of a value.
 *
 * @param value - Value to invert.
 * @returns Boolean opposite of the value.
 */
Handlebars.registerHelper('not', function (value: unknown) {
  return !value;
});

/**
 * Checks strict equality.
 *
 * @param a - First value.
 * @param b - Second value.
 * @returns True if values are strictly equal.
 */
Handlebars.registerHelper('eq', function (a: unknown, b: unknown) {
  return a === b;
});

/**
 * Checks strict inequality.
 *
 * @param a - First value.
 * @param b - Second value.
 * @returns True if values are not strictly equal.
 */
Handlebars.registerHelper('neq', function (a: unknown, b: unknown) {
  return a !== b;
});

/**
 * Checks if the first value is greater than the second.
 *
 * @param a - First value.
 * @param b - Second value.
 * @returns True if a is greater than b.
 */
Handlebars.registerHelper('gt', function (a: unknown, b: unknown) {
  return toSafeNumber(a) > toSafeNumber(b);
});

/**
 * Checks if the first value is greater than or equal to the second.
 *
 * @param a - First value.
 * @param b - Second value.
 * @returns True if a is greater than or equal to b.
 */
Handlebars.registerHelper('gte', function (a: unknown, b: unknown) {
  return toSafeNumber(a) >= toSafeNumber(b);
});

/**
 * Checks if the first value is less than the second.
 *
 * @param a - First value.
 * @param b - Second value.
 * @returns True if a is less than b.
 */
Handlebars.registerHelper('lt', function (a: unknown, b: unknown) {
  return toSafeNumber(a) < toSafeNumber(b);
});

/**
 * Checks if the first value is less than or equal to the second.
 *
 * @param a - First value.
 * @param b - Second value.
 * @returns True if a is less than or equal to b.
 */
Handlebars.registerHelper('lte', function (a: unknown, b: unknown) {
  return toSafeNumber(a) <= toSafeNumber(b);
});

/**
 * Returns the first value that is not null, undefined, or an empty string.
 *
 * @param args - Candidate fallback values.
 * @returns First usable value, or empty string.
 */
Handlebars.registerHelper('coalesce', function (...args: unknown[]) {
  const values = getHelperArgs(args);
  return (
    values.find(
      (value) => value !== null && value !== undefined && value !== '',
    ) ?? ''
  );
});

/**
 * Returns one of two values based on a condition.
 *
 * @param condition - Condition to evaluate.
 * @param truthyValue - Value returned when condition is truthy.
 * @param falsyValue - Value returned when condition is falsy.
 * @returns Selected value.
 */
Handlebars.registerHelper(
  'ternary',
  function (condition: unknown, truthyValue: unknown, falsyValue: unknown) {
    return condition ? truthyValue : falsyValue;
  },
);

/**
 * Performs basic math operations.
 *
 * Supported operators: +, -, *, /, %, round
 *
 * @param a - First value.
 * @param operator - Math operator.
 * @param b - Second value.
 * @returns Calculation result.
 */
Handlebars.registerHelper(
  'math',
  function (a: unknown, operator: string, b?: unknown) {
    const left = toSafeNumber(a);
    const right = toSafeNumber(b);

    switch (operator) {
      case '+':
        return left + right;
      case '-':
        return left - right;
      case '*':
        return left * right;
      case '/':
        return right === 0 ? 0 : left / right;
      case '%':
        return right === 0 ? 0 : left % right;
      case 'round':
        return Math.round(left);
      default:
        return 0;
    }
  },
);

/**
 * Converts a value into a formatted JSON string.
 *
 * @param value - Value to stringify.
 * @returns Pretty JSON string.
 */
Handlebars.registerHelper('json', function (value: unknown) {
  return JSON.stringify(value, null, 2);
});

/**
 * Checks if a value is empty.
 *
 * Empty means:
 * - null
 * - undefined
 * - empty string
 * - empty array
 * - empty object
 *
 * @param value - Value to check.
 * @returns True if value is empty.
 */
Handlebars.registerHelper('isEmptyValue', function (value: unknown) {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object') return Object.keys(value).length === 0;

  return false;
});

/**
 * PURE FUNCTION: Handlebars helper to check for strict equality.
 * Allows the AI to use conditional logic like {{#if (eq type 'bug')}} in its templates.
 * @param {any} a - The first value to compare.
 * @param {any} b - The second value to compare.
 * @returns {boolean} True if the values are strictly equal.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
Handlebars.registerHelper('eq', function (a: any, b: any) {
  return a === b;
});

/**
 * Configuration object for the Template Renderer module.
 * Stores localized error messages and configurable defaults.
 */
export const TEMPLATE_RENDERER_CONFIG = {
  errors: {
    compilationFailed: $localize`:@@templateRenderer.compilationFailed:Failed to compile the provided template.`,
    sanitizationFailed: $localize`:@@templateRenderer.sanitizationFailed:Failed to sanitize the generated HTML.`,
    emptyTemplate: $localize`:@@templateRenderer.emptyTemplate:No template provided.`,
  },
};

/**
 * PURE FUNCTION: Compiles a Handlebars template string using the provided data object.
 * Handles compilation errors gracefully by returning a safe error string.
 * CRITICAL FIX: Automatically strips out higher-tier responsive classes (md:, lg:, etc)
 * to forcefully simulate mobile and tablet viewports within the isolated preview containers.
 * @param {string} templateString - The raw Handlebars template string generated by the AI.
 * @param {Record<string, any>} data - The state data to interpolate into the template.
 * @param {'desktop' | 'tablet' | 'mobile'} simulateViewMode - The target responsive view.
 * @returns {string} The compiled HTML string, or an error message if compilation fails.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function compileTemplate(
  templateString: string,
  data: Record<string, any>,
  simulateViewMode: 'desktop' | 'tablet' | 'mobile' = 'desktop',
): string {
  if (
    !templateString ||
    typeof templateString !== 'string' ||
    templateString.trim() === ''
  ) {
    return '';
  }

  try {
    let processedTemplate = templateString;

    // Simulate mobile/tablet breakpoints by physically stripping higher-tier media query classes
    // This perfectly bypasses the need for @container and works natively with compiled Tailwind CSS
    if (simulateViewMode === 'mobile') {
      processedTemplate = processedTemplate.replace(
        /class="([^"]*)"/g,
        (match, classList) => {
          return `class="${classList.replace(/(?:^|\s)[^\s"]*(?:@?sm|@?md|@?lg|@?xl|@?2xl):[^\s"]+/g, '')}"`;
        },
      );
      processedTemplate = processedTemplate.replace(
        /class='([^']*)'/g,
        (match, classList) => {
          return `class='${classList.replace(/(?:^|\s)[^\s']*(?:@?sm|@?md|@?lg|@?xl|@?2xl):[^\s']+/g, '')}'`;
        },
      );
    } else if (simulateViewMode === 'tablet') {
      processedTemplate = processedTemplate.replace(
        /class="([^"]*)"/g,
        (match, classList) => {
          return `class="${classList.replace(/(?:^|\s)[^\s"]*(?:@?lg|@?xl|@?2xl):[^\s"]+/g, '')}"`;
        },
      );
      processedTemplate = processedTemplate.replace(
        /class='([^']*)'/g,
        (match, classList) => {
          return `class='${classList.replace(/(?:^|\s)[^\s']*(?:@?lg|@?xl|@?2xl):[^\s']+/g, '')}'`;
        },
      );
    }

    // Compile the string into a Handlebars execution function
    const templateDelegate = Handlebars.compile(processedTemplate, {
      noEscape: false, // Ensure Handlebars safely escapes HTML entities in variables by default
      strict: false, // Allow missing variables without throwing hard errors
    });

    // Execute the delegate with the provided data context
    return templateDelegate(data);
  } catch (error) {
    console.error('[TemplateRenderer] Handlebars Compilation Error:', error);
    return `<div class="text-danger font-semibold p-4 border border-danger/30 bg-danger/10 rounded-lg">
              <i class="fas fa-exclamation-triangle mr-2"></i> ${TEMPLATE_RENDERER_CONFIG.errors.compilationFailed}
            </div>`;
  }
}

/**
 * PURE FUNCTION: Sanitizes an HTML string to prevent Cross-Site Scripting (XSS) attacks.
 * Strips out dangerous tags (e.g., <script>, <object>) and inline event handlers (e.g., onload, onerror).
 * * @param {string} rawHtml - The potentially unsafe HTML string.
 * @returns {string} A deeply sanitized, safe HTML string ready for DOM insertion.
 */
export function sanitizeHtml(rawHtml: string): string {
  if (!rawHtml) return '';

  try {
    return DOMPurify.sanitize(rawHtml, {
      USE_PROFILES: { html: true },
      FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed'],
      FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover'],

      ADD_TAGS: ['rapider-chart', 'rapider-select', 'rapider-tooltip'],
      ADD_ATTR: [
        // Chart properties
        'chart-data',
        'chart-options',
        'chart-type',
        'options',
        'value',
        'placeholder',
        'state-key',

        // Tooltip properties
        'text',
        'placement',
        'color',

        // Mapped select properties
        'auto-clear-search-value',
        'allow-clear',
        'backdrop',
        'variant',
        'open',
        'auto-focus',
        'disabled',
        'dropdown-class-name',
        'dropdown-match-select-width',
        'server-search',
        'max-multiple-count',
        'mode',
        'not-found-content',
        'place-holder',
        'show-arrow',
        'show-search',
        'size',
        'status',
        'token-separators',
        'loading',
        'max-tag-count',
        'option-height-px',
        'option-overflow-size',
        'select-on-tab',
      ],
    });
  } catch (error) {
    console.error('[TemplateRenderer] DOMPurify Sanitization Error:', error);
    return '';
  }
}
